import { motion } from "framer-motion";
import {
  Sparkles,
  Shield,
  Sun,
  Cog,
  Layers,
  Armchair,
  Square,
  Truck,
} from "lucide-react";
import { services } from "../data/staticData";

const iconMap = {
  sparkles: Sparkles,
  shield: Shield,
  sun: Sun,
  cog: Cog,
  layers: Layers,
  armchair: Armchair,
  square: Square,
  truck: Truck,
};

const Services = () => {
  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="services-page">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black uppercase tracking-tighter mb-4 sm:mb-6 px-2"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-white">Nuestros </span>
            <span className="text-[#00A8FF] text-glow">Servicios</span>
          </motion.h1>
          <motion.p
            className="text-white/60 text-sm sm:text-base lg:text-lg font-light max-w-2xl mx-auto px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Descubre nuestra amplia gama de servicios de detailing premium
            diseñados para mantener tu vehículo en perfectas condiciones
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Sparkles;
              return (
                <motion.div
                  key={service.id}
                  className="glass-card group overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ scale: 1.01 }}
                  data-testid={`service-detail-${service.id}`}
                >
                  <div className="flex flex-col lg:flex-row">
                    <div className="relative lg:w-1/2 h-64 lg:h-auto overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0a1628] opacity-0 lg:opacity-100" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] to-transparent lg:opacity-0" />
                    </div>
                    <div className="lg:w-1/2 p-4 sm:p-6 lg:p-8 flex flex-col justify-center">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 glass rounded-lg flex items-center justify-center mb-4 sm:mb-6 group-hover:glow-blue transition-all duration-500">
                        <IconComponent className="w-6 h-6 sm:w-8 sm:h-8 text-[#00A8FF]" />
                      </div>
                      <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-2 sm:mb-4 group-hover:text-[#00A8FF] transition-colors">
                        {service.name}
                      </h3>
                      <p className="text-white/60 font-light leading-relaxed text-sm sm:text-base">
                        {service.description}
                      </p>
                      <div className="mt-6 pt-6 border-t border-white/10">
                        <div className="flex items-center gap-4">
                          <div className="w-2 h-2 bg-[#00A8FF] rounded-full animate-pulse" />
                          <span className="text-[#00A8FF] text-sm uppercase tracking-wider font-medium">
                            Servicio Disponible
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            className="glass-card p-6 sm:p-8 lg:p-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tighter mb-3 sm:mb-4">
              <span className="text-white">¿Necesitas </span>
              <span className="text-[#00A8FF]">más información?</span>
            </h2>
            <p className="text-white/60 font-light mb-6 sm:mb-8 text-sm sm:text-base">
              Contáctanos para obtener un presupuesto personalizado sin compromiso
            </p>
            <a href="/contacto" data-testid="services-contact-btn">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn-skew bg-[#00A8FF] text-[#050505] px-8 py-4 font-bold uppercase tracking-widest hover:shadow-[0_0_30px_rgba(0,168,255,0.5)] transition-all duration-300"
              >
                <span>Solicitar Presupuesto</span>
              </motion.button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
